import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { DatePicker, Multiselect } from 'react-widgets';
import 'react-widgets/lib/scss/react-widgets.scss';

import { loadDataSource, selectChoice, setReportValue } from '../actions/reportActions';
import { reportPlaceholder } from '../utils/constants';
import { DATE_TIME } from 'common/util/dateTime';
import styles from './ReportOptions.scss';

export const ReportOptions = connect(null, {
    loadDataSource,
    setReportValue,
    selectChoice,
})(({ reportOptions, reportData, loadDataSource, setReportValue, selectChoice }) => {
    return (
        <section className={styles.report} data-cy="ReportOptions">
            {reportOptions.map(reportOption => {
                return (
                    <ReportOption
                        key={reportOption.choice}
                        reportOption={reportOption}
                        reportData={reportData}
                        selectChoice={selectChoice}
                        setReportValue={setReportValue}
                        loadDataSource={loadDataSource}
                    />
                );
            })}
        </section>
    );
});

const ReportOption = ({ reportOption, reportData, loadDataSource, selectChoice, setReportValue }) => {
    const { choice, choices, label, hidden } = reportOption;

    if (hidden) {
        return null;
    }

    const isMultipleChoice = choices.length > 1;

    return (
        <section title={choice} className="report" data-cy="ReportOptions-report">
            <header data-cy="ReportOptions-label">{label}</header>
            {choices.map(reportChoice => {
                return (
                    <ReportChoice
                        key={reportChoice.id}
                        reportChoice={reportChoice}
                        reportData={reportData}
                        loadDataSource={loadDataSource}
                        isMultipleChoice={isMultipleChoice}
                        selectChoice={selectChoice}
                        setReportValue={setReportValue}
                    />
                );
            })}
        </section>
    );
};

const ReportChoice = ({ reportData, reportChoice, selectChoice, setReportValue, loadDataSource, isMultipleChoice }) => {
    const { id, choice, label, type, dataKey, isSelected, isLoading, selected, values } = reportChoice;
    const choiceId = `${choice}-${id}`;

    useEffect(() => {
        const fetchData = async () => {
            setReportValue(reportChoice.dataKey, null);
            if (reportChoice.isSelected) {
                await loadDataSource(reportChoice.source, reportData);
            }
        };

        fetchData();
    }, [reportChoice.isSelected]);

    const onSelectChange = async () => {
        if (!reportChoice.isSelected) {
            selectChoice(reportChoice.choice, reportChoice.id);
        }
    };

    return (
        <div className={styles.report__choice} data-cy="ReportOptions-div">
            {isMultipleChoice && (
                <label htmlFor={choiceId}>
                    <input id={choiceId} type="radio" name={choice} checked={isSelected} onChange={onSelectChange} data-cy="ReportOptions-input"/>
                    {label}
                </label>
            )}
            <ReportField
                disabled={!isSelected || isLoading}
                type={type}
                values={values}
                dataKey={dataKey}
                setReportValue={setReportValue}
                selected={selected}
            />
        </div>
    );
};

const ReportField = ({ disabled, type, values, dataKey, setReportValue, selected }) => {
    const Field = FieldTypeMap[type] || FieldTypeMap.default;

    return (
        <Field
            disabled={disabled}
            dataKey={dataKey}
            setReportValue={setReportValue}
            selected={selected}
            values={values}
            data-cy="ReportOptions-ReportField"
        />
    );
};

const NoField = () => null;
const MultiselectField = ({ disabled, values, dataKey, setReportValue, selected }) => {
    return (
        <Multiselect
            className={styles.multiselect}
            disabled={disabled}
            data={values}
            value={selected}
            onChange={value => setReportValue(dataKey, value)}
            placeholder={reportPlaceholder[dataKey]}
            textField="label"
            valueField="key"
            data-cy="ReportOptions-Multiselect"
        />
    );
};

const DatePickerField = ({ disabled, dataKey, setReportValue, selected }) => {
    const [date, setDate] = useState(null);

    useEffect(() => {
        if (!selected) {
            setDate(null);
        }
    }, [selected]);

    const onChange = date => {
        const endDate = moment(date).endOf('day').toISOString();

        setDate(date);
        setReportValue(dataKey, endDate);
    };

    return <DatePicker placeholder={DATE_TIME.DATE_ONLY_PLACEHOLDER} format={DATE_TIME.DATE_ONLY_FORMAT} disabled={disabled} value={date} onChange={onChange} />;
};

const FieldTypeMap = {
    default: NoField,
    multiselect: MultiselectField,
    datePicker: DatePickerField,
};
